import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import Swal from 'sweetalert2';

/**
 * Gestión de comercios.
 *
 * Presentación: LISTA/TABLA sobre el sistema de diseño `.kb-*` de `src/styles.css`
 * (tokens semánticos, sin Bootstrap y sin colores crudos en la plantilla).
 *
 * El filtrado, el orden y la paginación son EN EL CLIENTE, que alcanza porque los
 * comercios son unidades o decenas — no miles, como los clientes.
 */
@Component({
  selector: 'app-comercios-list',
  templateUrl: './comercios-list.component.html',
  styleUrls: ['./comercios-list.component.css']
})
export class ComerciosListComponent implements OnInit {
  /**
   * 🔴 COMISIÓN Y CUENTA BANCARIA, APAGADAS en esta pantalla (2026-08-21,
   * pedido del dueño del producto). Se van las dos columnas, las dos métricas,
   * los dos filtros rápidos y las dos marcas de pendiente en la fila.
   *
   * Con bandera y NO borradas: el dato se sigue trayendo del servidor, las
   * funciones `comision()`, `baseComision()`, `sinComision()` y `sinCuenta()`
   * quedan intactas, y volver a mostrarlo es poner esto en `true`.
   *
   * ⚠️ CONSECUENCIA QUE HAY QUE SABER: con esto apagado, esta pantalla ya no
   * avisa que a un comercio le falta la comisión o la cuenta. Hoy hay uno de
   * cada uno —Farmatodo sin comisión, y uno sin cuenta— y a esos NO se les
   * puede facturar ni liquidar. El pendiente sigue existiendo; lo que deja de
   * existir es el lugar donde se veía.
   */
  //
  // 🔴 ENCENDIDAS EL 2026-09-20, por decisión de Juan: «no deberíamos tener ocultos
  // los links de comisiones». Y la consecuencia anotada arriba era justo el motivo:
  // apagadas, esta pantalla dejaba de avisar a qué comercio le falta la comisión o
  // la cuenta — y a ésos no se les puede facturar ni liquidar. El pendiente existía
  // igual; lo que faltaba era el lugar donde se veía.
  readonly verComision = true;
  readonly verCuenta   = true;

  // 🔴 2026-09-24 — `PASOS_FICHA` y `pasoFicha()` se fueron con la columna «Aprobación»:
  // el paso de la ficha ahora se lee dentro de `estadoVenta()`, con los textos de la ficha.


  /** Habilitado = lo aprobaron alguna vez Y el acuerdo está vigente. */
  habilitado = (m: any): boolean => !!m?.approved_at && m?.state === 'ACTIVE';

  /**
   * ── EL ESTADO DE VENTA ─────────────────────────────────────────────────────
   *
   * 🔴 2026-09-24, aprobado por Juan: UNA columna reemplaza a «Estado» y a
   * «Aprobación». Las dos se contradecían en la misma fila —Epa salía «Activo»
   * en verde y al lado «No puede vender todavía»—, porque «Activo» es el estado
   * del ACUERDO y no dice si vende.
   *
   * 📍 Es la MISMA regla, con los mismos seis textos y colores, que la insignia
   * de la ficha (`comercios-detail.component.ts`, `calcularEstadoVenta`). Pasó
   * por vocabulario: «Estado de venta» ya existía en el código como
   * `estadoVenta`, y no se inventó ningún valor. Dos pantallas que muestran lo
   * mismo con dos reglas hacen dudar de cuál miente.
   */
  estadoVenta = (m: any): { texto: string; clase: string; icono: string; rango: number } => {
    const ficha = m?.profile_status || 'BORRADOR';
    if (m?.state === 'TERMINATED')
      return { texto: 'Relación terminada', clase: 'kb-badge-peligro', icono: 'fa-ban', rango: 6 };
    if (m?.state === 'SUSPENDED')
      return { texto: 'Suspendido', clase: 'kb-badge-alerta', icono: 'fa-pause-circle', rango: 5 };
    if (this.habilitado(m) && !m?.shops_count)
      return { texto: 'Sin tiendas', clase: 'kb-badge-alerta', icono: 'fa-store-slash', rango: 2 };
    if (this.habilitado(m))
      return { texto: 'Vendiendo', clase: 'kb-badge-exito', icono: 'fa-check-circle', rango: 1 };
    if (ficha === 'ENVIADA' || ficha === 'REVISADA')
      return { texto: 'Ficha en revisión', clase: 'kb-badge-alerta', icono: 'fa-hourglass-half', rango: 2 };
    if (ficha === 'CAMBIOS_PEDIDOS')
      return { texto: 'Ficha devuelta', clase: 'kb-badge-peligro', icono: 'fa-undo', rango: 3 };
    return { texto: 'Todavía no vende', clase: 'kb-badge-neutro', icono: 'fa-minus-circle', rango: 4 };
  };

  /** Los conteos de las tarjetas de arriba. Cuentan sobre TODOS, no sobre lo filtrado:
   *  la tarjeta dice cuántos hay, y tocarla es lo que filtra. */
  get conSinComision(): number { return (this.comercios || []).filter(m => this.sinComision(m)).length; }
  get conSinCuenta(): number   { return (this.comercios || []).filter(m => this.sinCuenta(m)).length; }

  /** La tarjeta «Comercios» no filtra por un pendiente: vuelve a mostrarlos todos. */
  verTodos = (): void => {
    this.filtros.pendiente = 'todos';
    this.aplicarFiltros();
  };

  /**
   * Cuántos todavía no pueden vender.
   *
   * 🔴 Es la decisión de esta pantalla, y por eso desde el rediseño del
   * 2026-09-20 va en la CINTA de arriba —no en una tarjeta de métrica—, con el
   * enlace que los deja a la vista. Antes el número se veía y no se podía
   * tocar: para saber cuáles eran había que buscarlos a mano en la tabla.
   */
  get sinHabilitar(): number {
    // 2026-09-24: cuenta también a los aprobados sin tiendas —tampoco pueden vender—; la misma
    // regla que la insignia (`estadoVenta`), para que la tarjeta y la columna digan lo mismo.
    return (this.comercios || []).filter((m: any) => this.estadoVenta(m).texto !== 'Vendiendo').length;
  }

  /**
   * Cuántas columnas muestra la tabla ahora mismo.
   *
   * 📍 Lo usan los `colspan` de «cargando» y «sin resultados». Estaba fijo en 8
   * y con dos columnas apagadas esas filas se estiraban más que la tabla: un
   * borde que sobresale se lee como que algo se rompió.
   *
   * ⚠️ DATO PARA QUIEN DECIDA, anotado el 2026-09-20 y NO tocado a propósito:
   * `verComision` y `verCuenta` son `readonly = true`, o sea constantes, así
   * que el ternario de abajo siempre devuelve 9 y los siete `*ngIf` de la
   * plantilla que las consultan —cinco de `verComision`, dos de `verCuenta`—
   * nunca pueden dar falso. Hoy no hace daño: es código que no se ejecuta.
   * Pero si lo que están esperando es un PERMISO («ver comisiones» según el
   * rol), el lugar donde cablearlo a `_auth` es este; y si no lo están
   * esperando, las siete condiciones y este ternario sobran. La decisión no
   * es del rediseño.
   */
  get columnasVisibles(): number {
    return 6 + (this.verComision ? 2 : 0);
  }


  comercios: any[] = [];
  /** El resultado de los filtros, ya ordenado. Es sobre esto que se cuenta. */
  filtrados: any[] = [];
  /** El tramo de `filtrados` que se dibuja. */
  paginados: any[] = [];
  loading = true;

  // 🔴 REDISEÑO 2026-09-20 — `guardando` se fue. Era el candado del botón de
  // guardar de cuando el alta y la edición vivían en un modal DENTRO de esta
  // pantalla; desde que son `…/merchants/nuevo` y `…/merchants/:code/editar`,
  // el que guarda es `comercios-form` y tiene el suyo. Aquí no quedaba ninguna
  // plantilla que lo leyera: se comprobó buscando el nombre en todo `src/`.

  filtros = { busqueda: '', estado: 'todos', pendiente: 'todos' };

  /** Orden de la tabla. Arranca por código, que es como se citan en los reportes. */
  orden = { campo: 'code', asc: true };

  pagina = 1;
  readonly porPagina = 20;

  metricas = { activos: 0, suspendidos: 0, terminados: 0 };

  // 🔴 2026-09-24 — `ESTADOS` se fue con la columna «Estado»: Suspendido (ámbar) y Relación
  // terminada (rojo) viven ahora en `estadoVenta()`, con la misma regla de color.


  constructor(
    private router: Router,
    private _merchant: MerchantService,
    public _auth: AuthService
  ) { }

  ngOnInit(): void { this.cargar(); }

  puede = (accion: string): boolean => this._auth.hasPermission(`config.comercios.${accion}`);

  cargar = async (): Promise<void> => {
    this.loading = true;
    try {
      /**
       * ⚠️ MEDIDO EL 2026-09-20 Y NO SE TOCÓ: los cuatro filtros de esta
       * pantalla trabajan EN MEMORIA. Acá se pide `getMerchants({})` sin un
       * solo parámetro y se trae la lista entera, aunque:
       *   · `MerchantService.getMerchants` ya arma `?search=` y `?state=`
       *     (merchant.service.ts:74-82), y
       *   · el back ya sabe filtrar por los dos
       *     (kredit-api-MT `controllers/merchant.js:45`, que lee
       *     `{ code, state, status, search }` del query).
       *
       * Hoy no duele —los comercios son unidades o decenas, no miles como los
       * clientes— y mandarlo al servidor CAMBIA EL COMPORTAMIENTO: el conteo
       * «X de Y», el orden y la paginación pasarían a depender de una llamada
       * por tecla. Es una decisión de producto, no de rediseño, y por eso
       * queda anotado en vez de arreglado.
       */
      const rep = await this._merchant.getMerchants({});
      this.comercios = rep?.merchants || [];
      this.calcularMetricas();
      this.aplicarFiltros();
    } catch (error: any) {
      Swal.fire({ icon: 'error', title: 'No se pudieron cargar los comercios',
                  text: error?.error?.msg || 'Intenta de nuevo en un momento' });
    } finally { this.loading = false; }
  };

  /**
   * El reparto por estado, que es el renglón de datos bajo el título.
   *
   * 🔴 REDISEÑO 2026-09-20. Quedaron TRES de los siete contadores que había.
   * Los cuatro que se fueron con sus tarjetas:
   *   · `total`       — el total ya está dos veces en la pantalla: en el conteo
   *                     de la barra de filtros («X de Y») y en el pie.
   *   · `tiendas`     — aquí no se decide nada con el total de tiendas de la
   *                     red; es un dato de Organización.
   *   · `sinComision` — el chip que filtra dice lo mismo y además sirve.
   *   · `sinCuenta`   — ídem.
   * Las funciones `sinComision(m)` y `sinCuenta(m)` siguen enteras: las usan
   * los chips, la marca de la fila y la columna de comisión.
   */
  calcularMetricas = (): void => {
    const c = this.comercios;
    this.metricas = {
      activos: c.filter(m => m.state === 'ACTIVE').length,
      suspendidos: c.filter(m => m.state === 'SUSPENDED').length,
      terminados: c.filter(m => m.state === 'TERMINATED').length
    };
  };

  // ── pendientes operativos ───────────────────────────────────────────────────

  /** Sin comisión no se le puede FACTURAR. Sin definir es un pendiente, no un cero. */
  sinComision = (m: any): boolean => {
    if (this.plazoPorOmision(m)) return false;
    const v = m?.terms_vigentes?.commission?.value;
    return v === null || v === undefined || v === '';
  };

  /**
   * 🔴 EL TARIFARIO MANDA SOBRE LA COMISIÓN SUELTA — la misma regla que la ficha
   * (`comercios-detail.component.ts`, `plazoPorOmision`). La lista leía sólo
   * `commission.value` y el 2026-09-24 mostraba a MT con 5 % mientras su ficha y
   * su acuerdo vigente (versión 3) dicen 3 %: el número viejo sobrevive en el
   * campo suelto y el vigente vive en `settlement.rates`. Dos pantallas que
   * muestran el mismo acuerdo no pueden leerlo de dos lugares distintos.
   */
  private plazoPorOmision = (m: any): any => {
    const activos = (m?.terms_vigentes?.settlement?.rates || [])
      .filter((r: any) => (r?.days || r?.term) && r.status !== false)
      .map((r: any) => ({
        ...r,
        days: Number(r.days) || ({ '24H': 1, '7D': 7, '15D': 15, '30D': 30 } as any)[r.term] || 7
      }))
      .sort((a: any, b: any) => a.days - b.days);
    return activos.find((r: any) => r.is_default) || activos[0] || null;
  };

  /** Sin cuenta bancaria no se le puede LIQUIDAR. */
  sinCuenta = (m: any): boolean => !m?.bank_account?.account_number;

  // 🔴 REDISEÑO 2026-09-20 — `tienePendientes(m)` se fue. Era el «o» de las dos
  // de arriba, y lo usaba la tarjeta de métrica «Con pendientes», que salió con
  // las otras cuatro. La fila no lo necesita: marca cada falta donde está —la
  // cuenta en la columna del comercio y la comisión en la suya— y el filtro
  // pregunta por UNA de las dos, nunca por «alguna». Verificado que ninguna
  // plantilla del repositorio lo nombra.

  // ── filtros, orden y paginación ─────────────────────────────────────────────

  aplicarFiltros = (): void => {
    const t = (this.filtros.busqueda || '').toLowerCase().trim();
    this.filtrados = this.comercios.filter(m => {
      const porEstado = this.filtros.estado === 'todos' || m.state === this.filtros.estado;
      const porTexto = !t ||
        [m.code, m.name, m.legal_name, m.tax_id].some((v: any) => (v || '').toLowerCase().includes(t));
      const porPendiente = this.filtros.pendiente === 'todos' ||
        // El pendiente que manda: todavía no puede vender. Es el que enciende
        // la cinta de arriba, y el enlace de la cinta pone este mismo filtro.
        (this.filtros.pendiente === 'sinHabilitar' && this.estadoVenta(m).texto !== 'Vendiendo') ||
        (this.filtros.pendiente === 'sinComision' && this.sinComision(m)) ||
        (this.filtros.pendiente === 'sinCuenta' && this.sinCuenta(m));
      return porEstado && porTexto && porPendiente;
    });
    this.ordenarLista();
    this.pagina = 1;
    this.paginar();
  };

  /** ¿Hay algún filtro puesto? Distingue "no hay comercios" de "el filtro no encontró nada". */
  get hayFiltros(): boolean {
    return !!(this.filtros.busqueda || '').trim() ||
           this.filtros.estado !== 'todos' ||
           this.filtros.pendiente !== 'todos';
  }

  limpiarFiltros = (): void => {
    this.filtros = { busqueda: '', estado: 'todos', pendiente: 'todos' };
    this.aplicarFiltros();
  };

  /** Los chips de pendientes son de un solo valor: volver a tocar el mismo lo apaga. */
  alternarPendiente = (valor: string): void => {
    this.filtros.pendiente = this.filtros.pendiente === valor ? 'todos' : valor;
    this.aplicarFiltros();
  };

  // 🔴 2026-09-24 — `verSinHabilitar()` era el enlace de la cinta; la cinta pasó a ser la
  // tarjeta «No pueden vender», que usa `alternarPendiente('sinHabilitar')`.


  ordenar = (campo: string): void => {
    if (this.orden.campo === campo) this.orden.asc = !this.orden.asc;
    else this.orden = { campo, asc: true };
    this.ordenarLista();
    this.pagina = 1;
    this.paginar();
  };

  iconoOrden = (campo: string): string =>
    this.orden.campo !== campo ? '' : (this.orden.asc ? 'fa-sort-up' : 'fa-sort-down');

  private ordenarLista = (): void => {
    const signo = this.orden.asc ? 1 : -1;
    const campo = this.orden.campo;
    this.filtrados.sort((a: any, b: any) => {
      let va: any; let vb: any;
      switch (campo) {
        case 'name':     va = (a.name || '').toLowerCase();  vb = (b.name || '').toLowerCase();  break;
        case 'venta':    va = this.estadoVenta(a).rango;     vb = this.estadoVenta(b).rango;     break;
        case 'tiendas':  va = a.shops_count || 0;            vb = b.shops_count || 0;            break;
        case 'comision': va = this.valorComision(a);         vb = this.valorComision(b);         break;
        default:         va = (a.code || '').toLowerCase();  vb = (b.code || '').toLowerCase();
      }
      if (va < vb) return -signo;
      if (va > vb) return signo;
      return 0;
    });
  };

  /** Para ordenar: los que no tienen comisión quedan juntos, no valen cero. */
  private valorComision = (m: any): number => {
    if (this.sinComision(m)) return -1;
    const p = this.plazoPorOmision(m);
    return Number(p ? p.commission_value : m.terms_vigentes.commission.value);
  };

  private paginar = (): void => {
    const inicio = (this.pagina - 1) * this.porPagina;
    this.paginados = this.filtrados.slice(inicio, inicio + this.porPagina);
  };

  get totalPaginas(): number { return Math.max(1, Math.ceil(this.filtrados.length / this.porPagina)); }
  get desde(): number { return this.filtrados.length ? (this.pagina - 1) * this.porPagina + 1 : 0; }
  get hasta(): number { return Math.min(this.pagina * this.porPagina, this.filtrados.length); }

  irAPagina = (n: number): void => {
    if (n < 1 || n > this.totalPaginas) return;
    this.pagina = n;
    this.paginar();
  };

  // ── condiciones vigentes ────────────────────────────────────────────────────

  /** La comisión vigente, lista para mostrar. Sin definir es un pendiente, no un cero. */
  comision = (m: any): string => {
    const p = this.plazoPorOmision(m);
    if (p) return p.type_calc === 'FIXED' ? `$${p.commission_value}` : `${p.commission_value}%`;
    const c = m?.terms_vigentes?.commission;
    if (!c || c.value === null || c.value === undefined) return 'sin definir';
    return c.type_calc === 'PERCENTAGE' ? `${c.value}%` : `$${c.value}`;
  };

  /**
   * El plazo de cobro, en días, que acompaña a la comisión de la fila.
   *
   * 🔴 Misma regla que `comercios-detail.component.ts` (`plazoCobro`): el plazo
   * vive en `settlement.rates`, no en un campo aparte, y una comisión sin su
   * plazo se lee mal («3 %» solo, sin decir si es a 24 horas o a 15 días).
   */
  plazoCobro = (m: any): string => {
    const p = this.plazoPorOmision(m);
    if (p) return (p.label || '').trim() || (Number(p.days) === 1 ? '24 horas' : `${p.days} días`);
    // 📍 Legado: las condiciones anteriores al tarifario traen una frecuencia.
    const f = m?.terms_vigentes?.settlement?.frequency;
    return ({ DAILY: 'cada 24 horas', WEEKLY: 'cada 7 días',
              BIWEEKLY: 'cada 15 días', MONTHLY: 'cada mes' } as any)[f] || '—';
  };

  /** Un porcentaje sin base no significa nada: la base viaja pegada a la comisión. */
  baseComision = (m: any): string =>
    ({
      SALE:      'sobre el monto vendido',
      FINANCED:  'sobre el saldo a financiar',
      COLLECTED: 'sobre lo cobrado'
    } as any)[this.plazoPorOmision(m)?.base || m?.terms_vigentes?.commission?.base] || 'sobre el monto vendido';

  riesgo = (m: any): string => ({
    NO_RECOURSE: 'Sin recurso', RECOURSE: 'Con recurso', MIXED: 'Mixto'
  } as any)[m?.terms_vigentes?.risk_model?.type_risk] || '—';

  // ── navegación ──────────────────────────────────────────────────────────────
  //
  // El detalle y el formulario dejaron de vivir acá: son páginas con ruta propia.
  // El listado ya no carga el detalle de nadie — solo lleva al que se pidió.

  /** Abre la ficha. La fila entera es clickeable, no solo el ojito. */
  abrirDetalle = (m: any): void => { this.router.navigate(['/console/merchants', m.code]); };

  // 🔴 2026-09-28 — el comercio ya no nace acá: nace del alta. El botón lleva
  // directo a `console/altas/nueva`; la ruta vieja `console/merchants/nuevo`
  // queda de todos modos con un redirectTo, por si alguien la tiene guardada.
  nuevo = (): void => { this.router.navigate(['/console/altas/nueva']); };

  editar = (m: any): void => { this.router.navigate(['/console/merchants', m.code, 'editar']); };

  /**
   * Suspender NO es desactivar, y el texto lo dice: la cartera sigue viva y hay
   * que seguirle liquidando. Sin esa aclaración alguien va a creer que también
   * se detiene la cobranza.
   */
  cambiarEstado = async (m: any, estado: 'ACTIVE' | 'SUSPENDED' | 'TERMINATED'): Promise<void> => {
    const textos: any = {
      SUSPENDED: {
        title: `¿Suspender ${m.name}?`,
        html: `<p class="text-sm text-muted-foreground">El comercio <b>deja de poder vender</b>.</p>
               <p class="text-sm text-muted-foreground mt-2">Su cartera sigue activa y se le sigue liquidando
               lo pendiente: suspender no es dar de baja.</p>`,
        confirmButtonText: 'Sí, suspender', input: true
      },
      TERMINATED: {
        title: `¿Terminar el acuerdo con ${m.name}?`,
        html: `<p class="text-sm text-muted-foreground">Se cierra la relación comercial.</p>
               <p class="text-sm text-muted-foreground mt-2">La cartera existente se sigue cobrando y liquidando
               hasta que se termine de pagar.</p>`,
        confirmButtonText: 'Sí, terminar', input: true
      },
      ACTIVE: {
        title: `¿Reactivar ${m.name}?`,
        html: `<p class="text-sm text-muted-foreground">Vuelve a poder vender.</p>`,
        confirmButtonText: 'Sí, reactivar', input: false
      }
    };
    const t = textos[estado];
    const r = await Swal.fire({
      title: t.title, html: t.html, icon: estado === 'ACTIVE' ? 'question' : 'warning',
      input: t.input ? 'text' : undefined,
      inputLabel: t.input ? 'Motivo (queda registrado)' : undefined,
      inputValidator: t.input ? (v: string) => !v?.trim() ? 'El motivo es obligatorio' : null : undefined,
      showCancelButton: true, confirmButtonText: t.confirmButtonText, cancelButtonText: 'Cancelar'
    });
    if (!r.isConfirmed) return;

    try {
      const rep = await this._merchant.setState(m.code, estado, r.value || undefined);
      await this.cargar();
      Swal.fire({ icon: 'success', title: rep?.msg || 'Estado actualizado',
                  text: rep?.aviso, timer: rep?.aviso ? undefined : 1600,
                  showConfirmButton: !!rep?.aviso });
    } catch (error: any) {
      Swal.fire({ icon: 'error', title: 'No se pudo cambiar el estado del comercio',
                  text: error?.error?.msg || 'Intenta de nuevo en un momento' });
    }
  };

  /** Eliminar. El back se niega si tiene tiendas o ventas, y el mensaje lo explica. */
  eliminar = async (m: any): Promise<void> => {
    const r = await Swal.fire({
      title: `¿Eliminar ${m.name}?`,
      html: `<p class="text-sm text-muted-foreground">Solo se puede eliminar un comercio
             <b>sin tiendas y sin ventas</b>. Esta acción no se puede deshacer.</p>
             <p class="text-sm text-muted-foreground mt-2">Si ya operó, lo que corresponde es
             <b>terminar el acuerdo</b>, que conserva la historia.</p>`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Eliminar', cancelButtonText: 'Cancelar'
    });
    if (!r.isConfirmed) return;
    try {
      await this._merchant.deleteMerchant(m.code);
      await this.cargar();
      Swal.fire({ icon: 'success', title: 'Comercio eliminado', timer: 1600, showConfirmButton: false });
    } catch (error: any) {
      Swal.fire({ icon: 'info', title: 'No se puede eliminar',
                  text: [error?.error?.msg, error?.error?.detalle].filter(Boolean).join('. ') });
    }
  };
}
